import React, { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import SiteChrome from "@/components/SiteChrome";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

// The admin editor stores rich-text HTML. Legacy content is plaintext with
// line breaks. Detect the format so we can render both without double-escaping.
function looksLikeHtml(s) {
  return /<\/?(p|h[1-6]|ul|ol|li|br|div|span|strong|em|a|b|i|u|table|thead|tbody|tr|td|th)\b/i.test(s || "");
}

// Print-only CSS: strips SiteChrome (header/nav/sponsors) and forces the
// content card to fit an A4 sheet with sensible margins and print-safe
// typography. Kept inline so the Regulamento page ships one-file.
//
// The stored HTML content (imported from a legacy CustomPage) wraps the
// rules text in nested `mx-auto max-w-3xl xl:max-w-4xl 2xl:max-w-5xl`
// containers that end up limiting the effective reading width to ~768px
// even though the outer card is A4 (794px). Neutralise those legacy
// max-width caps so the rules always fill the A4 sheet.
const PRINT_CSS = `
[data-testid="regulamento-content"] .max-w-3xl,
[data-testid="regulamento-content"] .max-w-4xl,
[data-testid="regulamento-content"] .max-w-5xl,
[data-testid="regulamento-content"] .xl\\:max-w-4xl,
[data-testid="regulamento-content"] .\\32xl\\:max-w-5xl {
  max-width: none !important;
  width: 100% !important;
}
[data-testid="regulamento-content"] .mx-auto { margin-left: 0 !important; margin-right: 0 !important; }
[data-testid="regulamento-content"] .px-4,
[data-testid="regulamento-content"] .md\\:px-8 { padding-left: 0 !important; padding-right: 0 !important; }

@media print {
  @page { size: A4; margin: 15mm; }
  html, body { background: #ffffff !important; }
  /* Hide everything not part of the regulamento container. */
  body * { visibility: hidden !important; }
  [data-print-root], [data-print-root] * { visibility: visible !important; }
  [data-print-root] {
    position: absolute; top: 0; left: 0; right: 0;
    padding: 0 !important; margin: 0 !important;
    max-width: 100% !important;
  }
  /* Content card: drop the shadow/border/rounded corners so the printed
     page is a clean sheet without a picture-frame around the text. */
  [data-print-root] [data-testid="regulamento-content"] {
    box-shadow: none !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 0 !important;
    background: #ffffff !important;
    color: #111827 !important;
  }
  [data-print-root] h1 { color: #111827 !important; }
  /* Avoid orphan headings at the bottom of a page. */
  [data-print-root] h1, [data-print-root] h2, [data-print-root] h3 {
    break-after: avoid-page;
    page-break-after: avoid;
  }
  [data-print-root] p, [data-print-root] li { orphans: 3; widows: 3; }
  /* Tables: keep visible borders when printed. */
  [data-print-root] table { border-collapse: collapse !important; width: 100% !important; }
  [data-print-root] th, [data-print-root] td {
    border: 1px solid #94a3b8 !important;
    padding: 6px 8px !important;
    color: #111827 !important;
  }
  [data-print-root] th { background: #f1f5f9 !important; }
  [data-print-root] table, [data-print-root] tr { break-inside: avoid; page-break-inside: avoid; }
  .no-print { display: none !important; }
}
`;

export default function Regulamento() {
  const { t, lang } = useI18n();
  const [content, setContent] = useState("");

  useEffect(() => {
    api
      .get("/content/regulamento", { params: { lang } })
      .then((r) => setContent(r.data.content || ""))
      .catch(() => setContent(""));
  }, [lang]);

  const isHtml = looksLikeHtml(content);

  const handlePrint = () => {
    if (typeof window !== "undefined") window.print();
  };

  const printLabel = lang === "en"
    ? "Print / Save as PDF"
    : lang === "es" ? "Imprimir / Guardar PDF" : "Imprimir / Guardar PDF";

  return (
    <SiteChrome>
      <style dangerouslySetInnerHTML={{ __html: PRINT_CSS }} />
      <div className="min-h-screen bg-slate-50 font-[Nunito]">
        {/* Reading column matches the reference site
            (https://eudactica.com/p/rsupertmatik): responsive Tailwind
            container that scales from `max-w-3xl` on small screens up to
            `max-w-5xl` on wide screens, with `px-4 md:px-8` gutters. */}
        <div
          className="mx-auto px-4 md:px-8 w-full max-w-3xl xl:max-w-4xl 2xl:max-w-5xl py-12"
          data-print-root
        >
          <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
            <h1
              className="text-4xl font-[Fredoka] font-semibold text-slate-900"
              data-testid="regulamento-title"
            >
              {t("rules_title")}
            </h1>
            <Button
              onClick={handlePrint}
              variant="outline"
              className="no-print rounded-full border-slate-300 text-slate-700 hover:bg-slate-100 shrink-0"
              data-testid="regulamento-print-btn"
              aria-label={printLabel}
            >
              <Printer className="w-4 h-4 mr-2" />
              {printLabel}
            </Button>
          </div>
          {isHtml ? (
            <div
              className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm leading-relaxed text-slate-700 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-2 [&_a]:text-sky-600 [&_a]:underline [&_table]:border-collapse [&_table]:w-full [&_table]:my-4 [&_th]:border [&_th]:border-slate-300 [&_th]:bg-slate-100 [&_th]:p-2 [&_th]:text-left [&_th]:font-semibold [&_td]:border [&_td]:border-slate-300 [&_td]:p-2 [&_td]:align-top"
              data-testid="regulamento-content"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          ) : (
            <div
              className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm whitespace-pre-wrap leading-relaxed text-slate-700"
              data-testid="regulamento-content"
            >
              {content}
            </div>
          )}
        </div>
      </div>
    </SiteChrome>
  );
}
